import React, { useState, useContext, useRef } from "react";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import usePageTitle from "./usePageTitle";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../AuthContext/AuthContext";
import AuthService from "../AuthContext/AuthService";
import "./Signin.css";

const SignIn = () => {
  usePageTitle("Login - SkeyeForm™");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [user, setUser] = useState({ username: "", password: "" });
  const [loading, setLoading] = useState(false);
  const toast = useRef(null);
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);

  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    if (!user.username || !user.password) {
      toast.current.show({
        severity: "warn",
        summary: "Required Fields",
        detail: "Please fill in both username and password.",
        life: 3000,
      });
      setLoading(false);
      return;
    }

    try {
      const response = await AuthService.login(user);
      const { isAuthenticated, user: loggedInUser } = response;

      if (isAuthenticated) {
        toast.current.show({
          severity: "success",
          summary: "Login Successful",
          detail: "Redirecting to the Home page.",
          life: 3000,
        });
        await new Promise((resolve) => setTimeout(resolve, 1500));
        authContext.setUser(loggedInUser);
        authContext.setIsAuthenticated(true);
        navigate("/");
      } else {
        toast.current.show({
          severity: "error",
          summary: "Login Failed",
          detail: "Wrong Credentials or Server Error. Please try again.",
          life: 3000,
        });
      }
    } catch (error) {
      console.error("Login error:", error);
      if (error.response) {
        toast.current.show({
          severity: "error",
          summary: "Login Failed",
          detail:
            error.response.data.message ||
            "Wrong Credentials. Please try again.",
          life: 3000,
        });
      } else if (error.request) {
        toast.current.show({
          severity: "error",
          summary: "Network Error",
          detail:
            "No response from server. Please check your network connection.",
          life: 3000,
        });
      } else {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail:
            error.message || "An unexpected error occurred. Please try again.",
          life: 3000,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="signin-container">
      <Toast ref={toast} />
      <div className="signin-card">
        <div className="text-center mb-5">
          <img
            src="/img/skeyeform_initial_logo.svg"
            alt="logo"
            height={150}
            className="mb-3 h"
          />
          <div className="signin-title">
            <span className="text-orange-500">Skeye </span>
            <span className="text-blue-900">Form</span>™
          </div>
        </div>

        <div>
          <label htmlFor="username" className="block text-900 font-medium mb-2">
            Username
          </label>
          <InputText
            id="username"
            type="text"
            placeholder="Username"
            className="w-full mb-3"
            value={user.username}
            onChange={(e) => setUser({ ...user, username: e.target.value })}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                onSubmit(e);
              }
            }}
          />

          <label htmlFor="password" className="block text-900 font-medium mb-2">
            Password
          </label>
          <div className="password-container">
            <InputText
              id="password"
              type={passwordVisible ? "text" : "password"}
              placeholder="Password"
              className="w-full"
              value={user.password}
              onChange={(e) => setUser({ ...user, password: e.target.value })}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  onSubmit(e);
                }
              }}
            />
            <Button
              icon={passwordVisible ? "pi pi-eye-slash" : "pi pi-eye"}
              onClick={togglePasswordVisibility}
              className="p-button-text"
              type="button"
            />
          </div>

          <Button
            label={loading ? "Loading..." : "Sign In"}
            disabled={loading}
            onClick={onSubmit}
            icon="pi pi-user"
            className="w-full font-bold signin-button"
          />
        </div>
      </div>
    </div>
  );
};

export default SignIn;
